import React, { useCallback } from "react";
import { Grant, useTenantUserPermisionsQuery } from "generated/graphql";
import { Redirect, useUnsafeTenantId } from "polyrouter";
import { PermissionsContext } from "./Context";
import { useAuth0 } from "@auth0/auth0-react";
import { Loader } from "../elements";

export function hasPermissionFromGrant(permission: string, grant?: Grant) {
  if (!grant) return false;
  const grantHasPermission = grant.permissions.some(
    g => g.name === permission || g.name === "*"
  );
  if (grantHasPermission) return true;
  const roleHasPermission = grant.roles.some(r =>
    r.permissions.some(perm => perm.name === permission || perm.name === "*")
  );
  if (roleHasPermission) return true;
  return false;
}

function useGrants() {
  const { user } = useAuth0();
  const userId = user ? user.sub : "";
  const { data, error, fetching } = useTenantUserPermisionsQuery({
    variables: { userId },
    pause: !userId
  });
  const grants = data?.accessor?.grants as Grant[];
  return {
    error,
    fetching,
    grants
  };
}

export function usePolyAdmin() {
  const { grants } = useGrants();
  if (grants) {
    const tenantResourceGrant = grants.find(
      grant => grant.resourceId === "*" && grant.resourceType === "Tenant"
    );
    const wildcardResourceGrant = grants.find(
      grant => grant.resourceId === "*" && grant.resourceType === "*"
    );
    const wildcardGrants = [];
    if (tenantResourceGrant) wildcardGrants.push(tenantResourceGrant);
    if (wildcardResourceGrant) wildcardGrants.push(wildcardResourceGrant);
    const isPolyAdmin =
      wildcardGrants.length > 0 &&
      wildcardGrants.some(grant =>
        grant.roles.some(r => r.name === "poly-admin")
      );
    return isPolyAdmin;
  }
  return false;
}

export function usePermissionsLogic() {
  const tenantId = useUnsafeTenantId();
  const { error, fetching, grants } = useGrants();

  const hasPermission = useCallback(
    (
      permission: string,
      /** this can be a tenant id, service name, etc */
      resourceId?: string | null | undefined
    ) => {
      if (!resourceId) {
        resourceId = tenantId;
      }
      if (!grants) return false;
      const findGrant = (
        resourceId: string | null | undefined,
        resourceType: "Tenant" | "Service" | "*"
      ) =>
        resourceId
          ? grants.find(
              grant =>
                grant.resourceId === resourceId &&
                grant.resourceType === resourceType
            )
          : undefined;
      if (hasPermissionFromGrant(permission, findGrant(resourceId, "Tenant")))
        return true;
      if (hasPermissionFromGrant(permission, findGrant(resourceId, "*")))
        return true;
      if (
        hasPermissionFromGrant(
          permission,
          findGrant("license-server", "Service")
        )
      )
        return true;
      if (
        hasPermissionFromGrant(
          permission,
          findGrant("clariti-trial", "Service")
        )
      )
        return true;
      if (hasPermissionFromGrant(permission, findGrant("*", "Tenant")))
        return true;
      if (hasPermissionFromGrant(permission, findGrant("*", "*"))) return true;
      return false;
    },
    [grants, tenantId]
  );

  return {
    error,
    fetching,
    hasPermission
  };
}

/**
 * Returns the list of permissions that the user has or an empty array if they have none.
 * Depends on the user being logged in and having a valid selected tenant.
 */

/**
 * Sets up the app permissions data.
 * Requires Redux and Graphql.
 * @param children
 * @constructor
 */
export const PermissionsProvider: React.FC = ({ children }) => {
  const { error, fetching, hasPermission } = usePermissionsLogic();
  return !fetching && error ? (
    <Redirect to="/error" />
  ) : (
    <PermissionsContext.Provider value={hasPermission}>
      <Loader className="theme-independent" loading={fetching}>
        {children}
      </Loader>
    </PermissionsContext.Provider>
  );
};
